/**
 * HLS-Session lifecycle (zapd-only, 2026-05-06).
 *
 * zapd merkt selbst wann niemand mehr Fragments fetched (Back-Pressure)
 * und räumt nach 5 min idle die Session ab (Reaper). Daher kein
 * heartbeat-loop nötig — fragment-fetches sind das implizite liveness-
 * signal. Nur sendBeacon-stop bei pagehide für SOFORTIGE Freigabe.
 *
 *   - pagehide → immediate sendBeacon. Last chance vor page-destruct.
 *
 * 2026-05-08 FIX (lifecycle-trace): visibilitychange + HIDDEN_GRACE_MS
 * entfernt. Original-intent war „kill bei tab-close", aber visibility=
 * hidden feuert auch bei JEDEM alt-tab. User-test 2026-05-08 zeigte:
 * 5 s nach alt-tab killte stopBeacon eine aktive playback-session
 * (last_fetched_seg=43, last_fetch_age_ms=4.4s ⇒ playback aktiv).
 * Ergebnis: jeder kurzer tab-switch reset playback-session → 404 →
 * recoverSession → user-sichtbarer rück-jump bei rückkehr.
 *
 * pagehide reicht für genuine page-destruction (= browser fires it bei
 * tab-close, navigation, refresh). Plus server-reaper bei idle 300 s
 * fängt vergessene sessions ab.
 *
 * sendBeacon kann nur POST → drogon hat /play/{sid}/stop-route die
 * intern markForDeletion macht (gleicher reaper-pfad wie DELETE).
 */
import {
  start as backendStart,
  stopBeacon as backendStopBeacon,
  stopAsync as backendStopAsync,
  fetchState as backendFetchState,
  keyframes as backendKeyframes,
} from './backend.js';
import { probeClientCaps, formatCapsSummary } from './decode/capabilities.ts';

/**
 * @typedef {Object} SessionHandles
 * @property {string} sessionHash       — session-id
 * @property {string} playlistUrl       — master.m3u8
 * @property {string|null} dashUrl
 * @property {()=>void} dispose
 */

/**
 * Open a new playback session and wire up tab-close hooks.
 *
 * @param {HTMLVideoElement} video
 * @param {{path:string, opts?:object,
 *          onPlaylistUrl?:(url:string)=>void}} cfg
 * @returns {Promise<SessionHandles>}
 */
export async function startHlsSession(video, cfg) {
  const opts = cfg.opts || {};

  // Phase 6a: client-capability handshake. Probe vor /play/start, damit
  // zapd weiß ob HEVC/AV1 passthrough möglich ist (= kein server-encode).
  // probeClientCaps() ist module-level cached, ~50-100 ms first-call.
  let clientCaps = null;
  try {
    clientCaps = await probeClientCaps();
    // eslint-disable-next-line no-console -- einmalige Startup-Caps-Diagnostik
    try { console.info('[zapd-caps]', formatCapsSummary(clientCaps)); } catch {}
  } catch (e) {
    // Fallback: ohne caps läuft server in legacy-transcode-mode.
    try { console.warn('[zapd-caps] probe failed, server falls back to transcode', e); } catch {}
  }

  const result = await backendStart({
    path: cfg.path,
    signal: cfg.signal,   // Player-Teardown bricht den /play/start-Call ab
    segdur: 4,
    transcode: typeof opts.transcode === 'string' ? opts.transcode : 'auto',
    // `audio` ist der GLOBALE source-stream-index (= aus probe.audio_streams[].index),
    // NICHT eine logische audio-track-nummer. Der zap-player resolved den
    // ordinal-pick (= playerSettings.audioTrack) zu diesem stream-index
    // bevor er hier ankommt. opts.audio_track bleibt für legacy-callers
    // als fallback.
    audio: typeof opts.audio === 'number' ? opts.audio
         : typeof opts.audio_track === 'number' ? opts.audio_track : undefined,
    audio_lang:       typeof opts.audio_lang === 'string' ? opts.audio_lang : undefined,
    audio_streams:    opts.audio_streams || undefined,
    subtitle_streams: opts.subtitle_streams || undefined,
    client_caps:      clientCaps || undefined,
    abr:              typeof opts.abr === 'number' ? opts.abr : undefined,
  });

  const sessionHash = result.sessionId;
  const playlistUrl = result.playlistUrl;
  if (!sessionHash || !playlistUrl) {
    throw new Error('zapd start: missing session_id/playlist_url');
  }

  let disposed = false;

  function onPagehide() {
    // Page is dying — sendBeacon is the only delivery that survives.
    backendStopBeacon(sessionHash);
  }
  window.addEventListener('pagehide', onPagehide);

  // "Nur sichtbarer Content spielt": pause (NOT stop) when the tab/page is
  // hidden. Pause is client-side (video.pause → engine.pause → server back-
  // pressure parks the encoder), the session stays alive so the return is
  // instant.
  //
  // KEIN Auto-Resume bei Rueckkehr (Produkt-Entscheid Team C, 2026-06-14):
  // der User klickt Play. Grund: AudioContext.resume braucht eine User-Geste —
  // ein Resume ohne Klick wird oft geblockt → Video laeuft, Ton bleibt
  // suspendiert; ausserdem soll Content nicht ungefragt losspielen
  // (YouTube/Plex-Video-Verhalten). Background-Audio fuer Musik kommt spaeter
  // media-typ-abhaengig (haengt an Team-B-Schema S2).
  //
  // DO NOT stop/kill here — that was the 2026-05-08 regression (visibility=
  // hidden stopBeacon killed active playback on every alt-tab → 404 →
  // recoverSession → visible back-jump). Pausing has none of that: no
  // server call, fully reversible.
  function onVisibility() {
    if (document.hidden && !video.paused && !video.ended) video.pause();
  }
  document.addEventListener('visibilitychange', onVisibility);

  function dispose() {
    if (disposed) return;
    disposed = true;
    window.removeEventListener('pagehide', onPagehide);
    document.removeEventListener('visibilitychange', onVisibility);
    // Explicit teardown (modal close / re-attach): the page stays alive, so
    // sendBeacon is best-effort and the browser frequently drops it →
    // sessions leaked until the 300s server-reaper ("3x streaming"). The
    // synchronous DELETE guarantees server-side stop. (pagehide keeps the
    // beacon — the only option once the page is being destroyed.)
    backendStopAsync(sessionHash);
  }

  if (cfg.onPlaylistUrl) try { cfg.onPlaylistUrl(playlistUrl); } catch {}

  // ── Seek-readiness polling (cold-cache-miss flow) ────────────────────
  // Initial-response hat seek_ready=true bei cache-hit oder false bei
  // cache-miss. Im miss-Fall pollen wir alle 1s, bis das Backend
  // seek_ready meldet (= async-keyframe-scan ist fertig). Frontend
  // signalisiert das via `cfg.onSeekReady(true)` damit zap-player.js
  // den scrub-bar enablen kann.
  // M1.5 — server-declared seekable (= persistent for the session,
  // distinct from the transient keyframe-cache-ready signal). When
  // false, the scrub-bar stays disabled even after keyframe-cache
  // completes. Used by the future "play-during-extract" scenario;
  // legacy responses without the field default to seekable=true.
  const sourceSeekable = result.seekable !== false;
  let seekReady = !!result.seekReady && sourceSeekable;
  let etaMs = Number(result.keyframesEtaMs || 0);
  // 2026-05-08: cached keyframe-PTS-array (microseconds) for client-side
  // VLC-fast-seek (= snap drop-position to nearest keyframe). Filled
  // once seek_ready=true triggers fetch via /keyframes endpoint.
  let keyframesUs = null;   // null until fetched
  let keyframesFetching = false;
  async function maybeFetchKeyframes() {
    if (keyframesUs !== null || keyframesFetching || !seekReady) return;
    keyframesFetching = true;
    try {
      const data = await backendKeyframes(sessionHash);
      if (data && Array.isArray(data.keyframes_us)) {
        keyframesUs = data.keyframes_us;
      }
    } catch (_e) { /* swallow — fast-seek falls back to direct-PTS-seek */ }
    finally { keyframesFetching = false; }
  }
  if (cfg.onSeekReadyChange) {
    try { cfg.onSeekReadyChange(seekReady, etaMs); } catch {}
  }
  // Z4 (Contract 02 §4.1): initiales source:{kind,progress}-Snapshot ans
  // Source-Badge. Updates folgen aus dem /state-Poll (s.source).
  if (cfg.onSourceChange && result.source) {
    try { cfg.onSourceChange(result.source); } catch {}
  }
  if (seekReady) maybeFetchKeyframes();
  let phase = '';
  let pollTimer = null;
  let pollFailures = 0;   // aufeinanderfolgende /state-Fehler (Backoff + Banner)
  function pollState() {
    if (disposed) return;
    backendFetchState(sessionHash, cfg.signal)
      .then((s) => {
        if (disposed) return;
        // Server wieder erreichbar → Backoff + Reconnect-Banner zuruecksetzen.
        if (pollFailures > 0) {
          pollFailures = 0;
          try { cfg.onConnectivity?.(true); } catch {}
        }
        if (s.seek_ready && !seekReady && sourceSeekable) {
          seekReady = true;
          etaMs = 0;
          if (cfg.onSeekReadyChange) {
            try { cfg.onSeekReadyChange(true, 0); } catch {}
          }
          // Lazy-load keyframes once scan completes — used for fast-seek
          // snap-to-keyframe in zap-player.seekTo.
          maybeFetchKeyframes();
        }
        // Decay-eta für UX („~Xs verbleibend") — nur während scan läuft.
        if (!seekReady && typeof s.keyframes_eta_ms === 'number'
            && s.keyframes_eta_ms !== etaMs) {
          etaMs = s.keyframes_eta_ms;
          if (cfg.onSeekReadyChange) {
            try { cfg.onSeekReadyChange(false, etaMs); } catch {}
          }
        }
        // Phase E: route lifecycle-phase + progress to UI overlay.
        // zapd publishes phase = init/mount/probe/cache_lookup/encode_warmup/
        // ready. Frontend overlay shows the corresponding message + a
        // progressbar driven by progress_pct + eta_ms. Once phase=ready,
        // overlay disappears.
        if (cfg.onPhaseChange && s.phase) {
          if (s.phase !== phase) {
            phase = s.phase;
          }
          try {
            cfg.onPhaseChange(s.phase, s.phase_message || '',
                              Number(s.progress_pct) || 0,
                              Number(s.eta_ms) || 0);
          } catch {}
        }
        // Z4: Live-Update des Source-Badges (Download-Fortschritt der
        // Torrent/RAR-Quelle), sofern der /state-Poll source liefert.
        if (cfg.onSourceChange && s.source) {
          try { cfg.onSourceChange(s.source); } catch {}
        }
        // 2026-05-08: nach seek_ready POLL FORTSETZEN als keepalive
        // (alle 60s). Server's /state-handler bumped last_fetched_steady_
        // ms, das hält reaper bei hidden-tabs / other-desktop-state
        // (RAF throttled, orchestrator fetched-pause) am leben. Ohne
        // dem würde reaper nach 5min idle die session killen wenn user
        // auf anderem desktop ist obwohl audio noch läuft.
        // Während phase != ready aggressiver pollen (= 250 ms) für
        // sofort-feedback im UI.
        const phase_ready = phase === 'ready';
        const next_ms = phase_ready ? (seekReady ? 60_000 : 1_000) : 250;
        pollTimer = setTimeout(pollState, next_ms);
      })
      .catch(() => {
        if (disposed) return;
        // Exp-Backoff+Jitter statt flat 2s-forever: ein neustartender/ueber-
        // lasteter zapd (oder nginx-Haenger) soll nicht im 2s-Takt gepingt
        // werden (Reconnect-Storm). Ab 2 Fehlern „Server neu verbinden"-Banner.
        pollFailures++;
        if (pollFailures >= 2) { try { cfg.onConnectivity?.(false); } catch {} }
        const base = Math.min(2000 * 2 ** (pollFailures - 1), 30000);
        const delay = Math.round(base * (0.85 + Math.random() * 0.3));   // ±15% Jitter
        pollTimer = setTimeout(pollState, delay);
      });
  }
  pollTimer = setTimeout(pollState, 200);

  // Erweiterte dispose: stop polling.
  const innerDispose = dispose;
  // eslint-disable-next-line no-func-assign -- dispose wird bewusst dekoriert (Poll-Stop)
  dispose = () => {
    if (pollTimer) { clearTimeout(pollTimer); pollTimer = null; }
    innerDispose();
  };

  return {
    sessionHash,
    playlistUrl,
    dashUrl: result.dashUrl,
    seekReady: () => seekReady,
    keyframesEtaMs: () => etaMs,
    keyframesUs: () => keyframesUs,   // null while not yet fetched
    dispose,
  };
}

/**
 * Phase-Label-Helper — zapd hat noch keine state-broadcasts, daher gibt
 * diese Funktion immer "" zurück. Wird von zap-player.js's setPhase()
 * aufgerufen — der phase-banner ist bei zapd dauerhaft unsichtbar
 * (player nutzt das `playing`-event auf <video> für „erstes Bild da").
 */
export function phaseLabel(_state) {
  return '';
}
